import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { MapPin, RefreshCw, AlertTriangle, TrendingUp } from 'lucide-react';

// Local Argentina TopoJSON with provinces (served from public folder)
const ARGENTINA_GEO_URL = "/geo/argentina-provinces.topo.json";

const GeoHeatmap = ({ embedded = false }) => {
  const [heatmapData, setHeatmapData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('30');
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [hoveredProvince, setHoveredProvince] = useState(null);

  useEffect(() => {
    loadHeatmapData();
  }, [periodo]);

  const loadHeatmapData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/geo/heatmap', {
        params: { dias: parseInt(periodo) }
      });
      setHeatmapData(res.data.heatmap_data);
      setSummary(res.data.summary);
    } catch (error) {
      console.error('Error loading heatmap:', error);
      toast.error('Error al cargar mapa de calor');
    } finally {
      setLoading(false);
    }
  };

  const getProvinceColor = (provinceName) => {
    // Find data for this province
    const data = heatmapData.find(d => 
      provinceName?.toUpperCase().includes(d.provincia?.toUpperCase()) ||
      d.provincia?.toUpperCase().includes(provinceName?.toUpperCase())
    );
    
    if (!data || data.total_alertas === 0) return '#f1f5f9';
    
    // Color scale based on intensity
    const intensity = data.intensity;
    if (intensity > 0.7) return '#dc2626'; // Red
    if (intensity > 0.5) return '#f97316'; // Orange
    if (intensity > 0.3) return '#eab308'; // Yellow
    if (intensity > 0.1) return '#84cc16'; // Light green
    return '#22c55e'; // Green
  };

  const getProvinceData = (provinceName) => {
    return heatmapData.find(d => 
      provinceName?.toUpperCase().includes(d.provincia?.toUpperCase()) ||
      d.provincia?.toUpperCase().includes(provinceName?.toUpperCase())
    );
  };

  // If embedded, show simplified version
  if (embedded) {
    return (
      <Card className="p-4 bg-white h-full">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <MapPin size={16} className="text-blue-600" />
            Mapa de Calor - Alertas por Provincia
          </h3>
          <Button variant="ghost" size="sm" onClick={loadHeatmapData} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        
        <div className="relative" style={{ height: '280px' }}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 400,
              center: [-64, -40]
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <Geographies geography={ARGENTINA_GEO_URL} parseGeographies={(geos) => geos}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  // Highcharts TopoJSON uses "name" property
                  const provinceName = geo.properties?.name || geo.properties?.NAME_1 || 'Unknown';
                  const data = getProvinceData(provinceName);
                  
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={getProvinceColor(provinceName)}
                      stroke="#94a3b8"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: 'none' },
                        hover: { outline: 'none', fill: '#60a5fa' },
                        pressed: { outline: 'none' }
                      }}
                      onMouseEnter={() => setHoveredProvince({ name: provinceName, data })}
                      onMouseLeave={() => setHoveredProvince(null)}
                    />
                  );
                })
              }
            </Geographies>
            
            {/* Markers for high-alert provinces */}
            {heatmapData.filter(d => d.total_alertas > 5).slice(0, 5).map((d, i) => (
              <Marker key={`${d.lat}-${d.lng}`} coordinates={[d.lng, d.lat]}>
                <circle r={4 + d.intensity * 6} fill="#ef4444" fillOpacity={0.6} />
              </Marker>
            ))}
          </ComposableMap>
          
          {/* Hover tooltip */}
          {hoveredProvince && (
            <div className="absolute top-2 left-2 bg-white p-2 rounded shadow-lg text-xs z-10">
              <p className="font-semibold">{hoveredProvince.name}</p>
              {hoveredProvince.data ? (
                <>
                  <p>Alertas: {hoveredProvince.data.total_alertas}</p>
                  <p>Críticas: {hoveredProvince.data.alertas_criticas}</p>
                </>
              ) : (
                <p className="text-slate-400">Sin alertas</p>
              )}
            </div>
          )}
        </div>
        
        {/* Mini legend */}
        <div className="flex justify-center gap-4 mt-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span>Bajo</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-yellow-500"></div>
            <span>Medio</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span>Alto</span>
          </div>
        </div>
      </Card>
    );
  }

  // Full page version
  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="text-blue-600" />
            Mapa de Calor Geográfico
          </h1>
          <p className="text-slate-600 mt-1">
            Distribución de alertas por ubicación geográfica
          </p>
        </div>
        <div className="flex gap-2 mt-4 lg:mt-0">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-40" style={{ backgroundColor: '#fff' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
              <SelectItem value="365">Último año</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={loadHeatmapData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin mr-2' : 'mr-2'} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-white">
          <div className="text-2xl font-bold text-slate-900">{summary.total_alertas || 0}</div>
          <div className="text-xs text-slate-500">Total Alertas</div>
        </Card>
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="text-2xl font-bold text-red-700">{summary.total_criticas || 0}</div>
          <div className="text-xs text-red-600">Críticas</div>
        </Card>
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="text-2xl font-bold text-blue-700">{summary.provincias_afectadas || 0}</div>
          <div className="text-xs text-blue-600">Provincias Afectadas</div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="text-2xl font-bold text-amber-700 truncate">{summary.provincia_mas_alertas || '-'}</div>
          <div className="text-xs text-amber-600">Mayor Concentración</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <Card className="lg:col-span-2 p-4 bg-white">
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Mapa de Argentina</h3>
          <div className="relative" style={{ height: '500px' }}>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{
                scale: 600,
                center: [-64, -40]
              }}
              style={{ width: '100%', height: '100%' }}
            >
              <Geographies geography={ARGENTINA_GEO_URL} parseGeographies={(geos) => geos}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    // Highcharts TopoJSON uses "name" property
                    const provinceName = geo.properties?.name || geo.properties?.NAME_1 || 'Unknown';
                    const data = getProvinceData(provinceName);
                    
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getProvinceColor(provinceName)}
                        stroke="#64748b"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: { outline: 'none', fill: '#3b82f6', cursor: 'pointer' },
                          pressed: { outline: 'none' }
                        }}
                        onClick={() => setSelectedProvince({ name: provinceName, data })}
                        onMouseEnter={() => setHoveredProvince({ name: provinceName, data })}
                        onMouseLeave={() => setHoveredProvince(null)}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
            
            {hoveredProvince && (
              <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-lg text-sm z-10 min-w-[150px]">
                <p className="font-semibold text-slate-900">{hoveredProvince.name}</p>
                {hoveredProvince.data ? (
                  <div className="mt-1 space-y-1 text-slate-600">
                    <p>Total: <strong>{hoveredProvince.data.total_alertas}</strong></p>
                    <p>Críticas: <strong className="text-red-600">{hoveredProvince.data.alertas_criticas}</strong></p>
                    <p>Altas: <strong className="text-orange-600">{hoveredProvince.data.alertas_altas}</strong></p>
                  </div>
                ) : (
                  <p className="text-slate-400 mt-1">Sin alertas registradas</p>
                )}
              </div>
            )}
          </div>
          
          {/* Legend */}
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span>Bajo (&lt;10%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500"></div>
              <span>Medio (10-50%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-500"></div>
              <span>Alto (50-70%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-600"></div>
              <span>Crítico (&gt;70%)</span>
            </div>
          </div>
        </Card>

        {/* Province List */}
        <Card className="p-4 bg-white">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp size={16} />
            Ranking por Provincia
          </h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {heatmapData.map((prov, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-lg cursor-pointer transition-colors ${
                  selectedProvince?.name === prov.provincia 
                    ? 'bg-blue-50 border border-blue-200' 
                    : 'bg-slate-50 hover:bg-slate-100'
                }`}
                onClick={() => setSelectedProvince({ name: prov.provincia, data: prov })}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-slate-900">{prov.provincia}</p>
                    <p className="text-xs text-slate-500">{prov.total_alertas} alertas</p>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-medium ${
                    prov.risk_level === 'ALTO' ? 'bg-red-100 text-red-700' :
                    prov.risk_level === 'MEDIO' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {prov.risk_level}
                  </div>
                </div>
                
                {/* Mini bar chart */}
                <div className="mt-2 flex gap-1 h-2">
                  <div 
                    className="bg-red-500 rounded-sm" 
                    style={{ width: `${(prov.alertas_criticas / Math.max(prov.total_alertas, 1)) * 100}%` }}
                    title={`Críticas: ${prov.alertas_criticas}`}
                  />
                  <div 
                    className="bg-orange-500 rounded-sm" 
                    style={{ width: `${(prov.alertas_altas / Math.max(prov.total_alertas, 1)) * 100}%` }}
                    title={`Altas: ${prov.alertas_altas}`}
                  />
                  <div 
                    className="bg-yellow-500 rounded-sm" 
                    style={{ width: `${(prov.alertas_medias / Math.max(prov.total_alertas, 1)) * 100}%` }}
                    title={`Medias: ${prov.alertas_medias}`}
                  />
                  <div 
                    className="bg-green-500 rounded-sm flex-1" 
                    title={`Bajas: ${prov.alertas_bajas}`}
                  />
                </div>
              </div>
            ))}
            
            {heatmapData.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <MapPin size={32} className="mx-auto mb-2 opacity-50" />
                <p>No hay datos disponibles</p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Selected Province Detail */}
      {selectedProvince?.data && (
        <Card className="mt-6 p-6 bg-white">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            Detalle: {selectedProvince.name}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-xs text-slate-500">Total Alertas</p>
              <p className="text-2xl font-bold text-slate-900">{selectedProvince.data.total_alertas}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Críticas</p>
              <p className="text-2xl font-bold text-red-600">{selectedProvince.data.alertas_criticas}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Altas</p>
              <p className="text-2xl font-bold text-orange-600">{selectedProvince.data.alertas_altas}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Medias</p>
              <p className="text-2xl font-bold text-yellow-600">{selectedProvince.data.alertas_medias}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Monto Total</p>
              <p className="text-2xl font-bold text-slate-900">
                ${selectedProvince.data.monto_total?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default GeoHeatmap;
